import React, { useState, useEffect, useCallback } from 'react';
import {
  AppWindow,
  Shield,
  Users,
  Search,
  Check,
  X,
  Sliders,
  AlertTriangle,
  RefreshCw,
  CheckCircle2,
  Lock,
  KeyRound,
  FileText,
  CreditCard,
  FolderLock,
  Terminal,
} from 'lucide-react';
import MainLayout from '../components/MainLayout';
import {
  adminListAllApplications,
  adminGetPermissionsMatrix,
  adminUpdateUserPermission,
  adminToggleApplication,
} from '../api/applications';
import { APP_ICON_MAP, APP_COLOR_MAP } from '../hooks/useApplications';

const PERMISSION_LEVELS = [
  { key: 'access', label: 'Access', desc: 'Can enter the application and see it in launcher' },
  { key: 'view', label: 'View', desc: 'Can view stored records and metadata' },
  { key: 'create', label: 'Create', desc: 'Can create new records' },
  { key: 'edit', label: 'Edit', desc: 'Can modify existing records' },
  { key: 'delete', label: 'Delete', desc: 'Can delete records from application' },
  { key: 'share', label: 'Share', desc: 'Can share records with team members' },
  { key: 'export', label: 'Export', desc: 'Can export plaintext records' },
  { key: 'reveal', label: 'Reveal', desc: 'Can unmask secrets, passwords, and CVV' },
  { key: 'copy', label: 'Copy', desc: 'Can copy credentials to clipboard' },
];

export default function AdminApplications() {
  const [apps, setApps] = useState([]);
  const [matrix, setMatrix] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [toastMsg, setToastMsg] = useState('');
  const [updating, setUpdating] = useState(false);

  // Granular Permission Modal State
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedAppSlug, setSelectedAppSlug] = useState('passwords');
  const [modalPermissions, setModalPermissions] = useState({});

  const showToast = (msg) => {
    setToastMsg(msg);
    setTimeout(() => setToastMsg(''), 3500);
  };

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [allApps, userMatrix] = await Promise.all([
        adminListAllApplications(),
        adminGetPermissionsMatrix(),
      ]);
      setApps(allApps || []);
      setMatrix(userMatrix || []);
    } catch (err) {
      showToast('Failed to load application permissions matrix');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleGlobalToggle = async (app) => {
    const nextState = !app.enabled;
    try {
      await adminToggleApplication(app.id, nextState);
      setApps((prev) =>
        prev.map((a) => (a.id === app.id ? { ...a, enabled: nextState } : a))
      );
      showToast(`${app.name} is now globally ${nextState ? 'ENABLED' : 'DISABLED'}`);
    } catch (err) {
      showToast(`Failed to toggle ${app.name}`);
    }
  };

  const handleQuickToggleAccess = async (user, appSlug) => {
    const currentPerms = user.permissions[appSlug] || { access: false };
    const nextAccess = !currentPerms.access;
    setUpdating(true);
    try {
      await adminUpdateUserPermission(user.user.id, appSlug, {
        ...currentPerms,
        access: nextAccess,
      });

      // Update local state
      setMatrix((prev) =>
        prev.map((row) => {
          if (row.user.id === user.user.id) {
            return {
              ...row,
              permissions: {
                ...row.permissions,
                [appSlug]: {
                  ...row.permissions[appSlug],
                  access: nextAccess,
                },
              },
            };
          }
          return row;
        })
      );
      showToast(`Updated ${appSlug} access for ${user.user.email}`);
    } catch (err) {
      showToast('Failed to update permission');
    } finally {
      setUpdating(false);
    }
  };

  const openPermissionModal = (userRow, appSlug = 'passwords') => {
    setSelectedUser(userRow);
    setSelectedAppSlug(appSlug);
    const existing = userRow.permissions[appSlug] || {};
    setModalPermissions({
      access: existing.access ?? true,
      view: existing.view ?? true,
      create: existing.create ?? true,
      edit: existing.edit ?? true,
      delete: existing.delete ?? true,
      share: existing.share ?? false,
      export: existing.export ?? false,
      reveal: existing.reveal ?? true,
      copy: existing.copy ?? true,
    });
  };

  const handleSaveModalPermissions = async (e) => {
    e.preventDefault();
    if (!selectedUser) return;
    setUpdating(true);
    try {
      await adminUpdateUserPermission(
        selectedUser.user.id,
        selectedAppSlug,
        modalPermissions
      );

      setMatrix((prev) =>
        prev.map((row) => {
          if (row.user.id === selectedUser.user.id) {
            return {
              ...row,
              permissions: {
                ...row.permissions,
                [selectedAppSlug]: { ...modalPermissions },
              },
            };
          }
          return row;
        })
      );

      showToast(`Permissions saved for ${selectedUser.user.email}`);
      setSelectedUser(null);
    } catch (err) {
      showToast('Failed to save permissions');
    } finally {
      setUpdating(false);
    }
  };

  const filteredMatrix = matrix.filter((row) => {
    const q = searchQuery.toLowerCase().trim();
    if (!q) return true;
    return (
      row.user.email.toLowerCase().includes(q) ||
      (row.user.role && row.user.role.toLowerCase().includes(q))
    );
  });

  // Highlighted core columns for the table
  const tableAppSlugs = [
    'passwords',
    'notes',
    'identities',
    'cards',
    'documents',
    'reminders',
    'tasks',
    'ssh-keys',
  ];

  return (
    <MainLayout>
      {toastMsg && (
        <div className="admin-toast admin-toast-success animate-fade-in" style={{ position: 'fixed', top: '24px', right: '24px', zIndex: 9999 }}>
          <CheckCircle2 size={16} />
          <span>{toastMsg}</span>
        </div>
      )}

      {/* Page Header - Full Bleed Edge-to-Edge */}
      <div className="admin-page-header notes-hero-banner">
        <div className="notes-hero-content">
          <div className="admin-header-title" style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <div className="admin-header-icon-badge notes-hero-icon-badge" style={{ background: 'rgba(255, 255, 255, 0.2)', color: '#ffffff' }}>
              <AppWindow size={24} />
            </div>
            <div>
              <h1 className="notes-hero-title">Application Permissions & Governance</h1>
              <p className="notes-hero-subtitle">
                Manage global availability, role defaults, and user-level authorization levels across all 12 modules.
              </p>
            </div>
          </div>
          <div className="admin-header-actions">
            <button
              className="btn-secondary notes-hero-btn-secondary"
              onClick={loadData}
              disabled={loading}
              title="Refresh permissions matrix"
            >
              <RefreshCw size={15} className={loading ? 'rotate-anim' : ''} />
              <span>Refresh</span>
            </button>
          </div>
        </div>
      </div>

      <div className="admin-page-container">
        {/* Global Master Application Toggles */}
        <div className="admin-section-card glass-panel" style={{ padding: '20px', borderRadius: '14px', marginBottom: '24px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <div>
              <h3 style={{ fontSize: '1.05rem', fontWeight: '600' }}>Global System Applications</h3>
              <p style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                Enable or disable modules across the entire organization. Disabled apps cannot be launched by anyone.
              </p>
            </div>
          </div>

          <div className="global-app-toggles-grid">
            {apps.map((app) => {
              const IconComponent = APP_ICON_MAP[app.slug] || APP_ICON_MAP[app.icon] || AppWindow;
              const brandColor = APP_COLOR_MAP[app.slug] || '#3b82f6';

              return (
                <div key={app.id} className={`global-toggle-pill ${app.enabled ? 'is-enabled' : 'is-disabled'}`}>
                  <div className="global-toggle-icon" style={{ background: `${brandColor}18`, color: brandColor }}>
                    <IconComponent size={16} />
                  </div>
                  <div className="global-toggle-copy">
                    <strong>{app.name}</strong>
                    <small>{app.enabled ? 'Enabled' : 'Disabled'}</small>
                  </div>
                  <button
                    type="button"
                    className={`toggle-switch-mini ${app.enabled ? 'on' : 'off'}`}
                    onClick={() => handleGlobalToggle(app)}
                    title={`Toggle ${app.name} globally`}
                  >
                    <span className="toggle-thumb" />
                  </button>
                </div>
              );
            })}
          </div>
        </div>

        {/* User Application Permissions Matrix */}
        <div className="admin-table-container glass-panel">
          <div className="admin-table-toolbar" style={{ padding: '16px 20px', borderBottom: '1px solid var(--border-color)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Users size={18} color="var(--text-secondary)" />
              <strong>User Application Permissions Matrix ({filteredMatrix.length} users)</strong>
            </div>

            <div className="admin-search-box" style={{ width: '280px', height: '36px', boxSizing: 'border-box' }}>
              <Search size={15} color="var(--text-secondary)" />
              <input
                type="text"
                placeholder="Search user or role..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="admin-search-input"
                aria-label="Search user or role"
              />
            </div>
          </div>

          <div style={{ overflowX: 'auto', width: '100%', WebkitOverflowScrolling: 'touch' }}>
            <table className="admin-table app-perms-table" style={{ width: '100%', minWidth: '960px' }}>
              <thead>
                <tr>
                  <th style={{ minWidth: '220px' }}>USER</th>
                  <th style={{ minWidth: '110px' }}>ROLE</th>
                  {tableAppSlugs.map((slug) => (
                    <th key={slug} style={{ textAlign: 'center', minWidth: '85px' }}>
                      {slug.toUpperCase().replace('-', ' ')}
                    </th>
                  ))}
                  <th style={{ textAlign: 'right', minWidth: '100px' }}>ACTIONS</th>
                </tr>
              </thead>
              <tbody>
                {filteredMatrix.map((row) => {
                  const u = row.user;
                  const isSuper = ['SUPER_ADMIN', 'ADMIN'].includes(u.role);

                  return (
                    <tr key={u.id}>
                      <td>
                        <div className="admin-user-cell">
                          <div className="user-avatar-sm" style={{ background: '#3b82f6' }}>
                            {u.email.charAt(0).toUpperCase()}
                          </div>
                          <div>
                            <strong>{u.email}</strong>
                            <small style={{ display: 'block', color: 'var(--text-secondary)' }}>
                              ID: {u.id.substring(0, 8)}...
                            </small>
                          </div>
                        </div>
                      </td>

                      <td>
                        <span className={`role-chip role-${(u.role || 'user').toLowerCase()}`}>
                          {u.role || 'USER'}
                        </span>
                      </td>

                      {tableAppSlugs.map((slug) => {
                        const perm = row.permissions[slug] || { access: false };
                        const hasAccess = isSuper || perm.access;

                        return (
                          <td key={slug} style={{ textAlign: 'center' }}>
                            <button
                              type="button"
                              className={`app-perm-cell-btn ${hasAccess ? 'granted' : 'revoked'}`}
                              onClick={() => {
                                if (isSuper) {
                                  showToast('Admins always have full access.');
                                  return;
                                }
                                handleQuickToggleAccess(row, slug);
                              }}
                              title={`${u.email}: Click to toggle ${slug} access, or use Edit for granular levels`}
                              disabled={isSuper || updating}
                            >
                              {hasAccess ? <Check size={14} /> : <X size={14} />}
                            </button>
                          </td>
                        );
                      })}

                      <td style={{ textAlign: 'right' }}>
                        <button
                          type="button"
                          className="btn-secondary btn-sm"
                          onClick={() => openPermissionModal(row)}
                          title="Open granular 9-level permission matrix"
                        >
                          <Sliders size={13} />
                          <span>Configure</span>
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* Granular Permission Editor Modal */}
        {selectedUser && (
          <div className="modal-backdrop" onClick={() => setSelectedUser(null)}>
            <div
              className="modal-card glass-panel"
              onClick={(e) => e.stopPropagation()}
              style={{ maxWidth: '640px' }}
            >
              <div className="modal-header">
                <div className="modal-header-icon" style={{ background: 'rgba(13, 148, 136, 0.12)', color: '#0d9488' }}>
                  <Sliders size={20} />
                </div>
                <div>
                  <h3>Application Permission Matrix</h3>
                  <p>
                    Configuring granular access for <strong>{selectedUser.user.email}</strong>
                  </p>
                </div>
                <button
                  type="button"
                  className="modal-close"
                  onClick={() => setSelectedUser(null)}
                  aria-label="Close modal"
                >
                  <X size={18} />
                </button>
              </div>

              <form onSubmit={handleSaveModalPermissions}>
                <div className="modal-body-content" style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                  {/* Select Application Tab Selector */}
                  <div className="form-group">
                    <label>Target Application</label>
                    <select
                      className="input-field"
                      value={selectedAppSlug}
                      onChange={(e) => {
                        const newSlug = e.target.value;
                        setSelectedAppSlug(newSlug);
                        const existing = selectedUser.permissions[newSlug] || {};
                        setModalPermissions({
                          access: existing.access ?? true,
                          view: existing.view ?? true,
                          create: existing.create ?? true,
                          edit: existing.edit ?? true,
                          delete: existing.delete ?? true,
                          share: existing.share ?? false,
                          export: existing.export ?? false,
                          reveal: existing.reveal ?? true,
                          copy: existing.copy ?? true,
                        });
                      }}
                    >
                      {apps.map((a) => (
                        <option key={a.slug} value={a.slug}>
                          {a.name} ({a.category})
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* 9 Permission Level Toggles */}
                  <div className="perms-matrix-list">
                    {PERMISSION_LEVELS.map((lvl) => {
                      const isChecked = Boolean(modalPermissions[lvl.key]);
                      return (
                        <div key={lvl.key} className="perm-toggle-row">
                          <div className="perm-toggle-copy">
                            <strong>{lvl.label}</strong>
                            <small>{lvl.desc}</small>
                          </div>
                          <button
                            type="button"
                            className={`toggle-switch ${isChecked ? 'on' : 'off'}`}
                            onClick={() =>
                              setModalPermissions((prev) => ({
                                ...prev,
                                [lvl.key]: !isChecked,
                              }))
                            }
                          >
                            <span className="toggle-thumb" />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div className="modal-actions" style={{ marginTop: '20px' }}>
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={() => setSelectedUser(null)}
                    disabled={updating}
                  >
                    Cancel
                  </button>
                  <button type="submit" className="btn-primary" disabled={updating}>
                    {updating ? 'Saving...' : 'Apply Permission Matrix'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </MainLayout>
  );
}
